import type { SupportTicketSummary } from '../../../services/helpTicketService';
import { HELP_TICKET_CATEGORIES } from './helpCategories';
import { ticketListIssueName } from './ticketContactInfo';

export const ADMIN_TICKET_STATUS_ORDER: SupportTicketSummary['status'][] = [
  'open',
  'in_progress',
  'completed',
];

export const ADMIN_TICKET_STATUS_LABELS: Record<SupportTicketSummary['status'], string> = {
  open: 'Open',
  in_progress: 'In progress',
  completed: 'Completed',
};

const CATEGORY_SORT_INDEX = new Map<string, number>(
  HELP_TICKET_CATEGORIES.map((category, index) => [category.value, index]),
);

export function compareTicketsByIssueType(
  a: SupportTicketSummary,
  b: SupportTicketSummary,
): number {
  const categoryIndexA = CATEGORY_SORT_INDEX.get(a.category) ?? HELP_TICKET_CATEGORIES.length;
  const categoryIndexB = CATEGORY_SORT_INDEX.get(b.category) ?? HELP_TICKET_CATEGORIES.length;
  if (categoryIndexA !== categoryIndexB) return categoryIndexA - categoryIndexB;

  const issueCompare = ticketListIssueName(a).localeCompare(
    ticketListIssueName(b),
    undefined,
    { sensitivity: 'base' },
  );
  if (issueCompare !== 0) return issueCompare;

  return b.createdAt.localeCompare(a.createdAt);
}

function sortStatusBuckets(group: StationTicketGroup): StationTicketGroup {
  return {
    ...group,
    byStatus: {
      open: [...group.byStatus.open].sort(compareTicketsByIssueType),
      in_progress: [...group.byStatus.in_progress].sort(compareTicketsByIssueType),
      completed: [...group.byStatus.completed].sort(compareTicketsByIssueType),
    },
  };
}

export interface StationTicketGroup {
  stationSlug: string;
  stationName: string;
  byStatus: Record<SupportTicketSummary['status'], SupportTicketSummary[]>;
}

export function countUnreadTicketsInStation(group: StationTicketGroup): number {
  return ADMIN_TICKET_STATUS_ORDER.reduce(
    (count, status) => count + group.byStatus[status].filter((t) => t.unreadForAdmin).length,
    0,
  );
}

export function groupTicketsByStationAndStatus(
  tickets: SupportTicketSummary[],
): StationTicketGroup[] {
  const map = new Map<string, StationTicketGroup>();

  for (const ticket of tickets) {
    let group = map.get(ticket.stationSlug);
    if (!group) {
      group = {
        stationSlug: ticket.stationSlug,
        stationName: ticket.stationName ?? ticket.stationSlug,
        byStatus: { open: [], in_progress: [], completed: [] },
      };
      map.set(ticket.stationSlug, group);
    }
    group.byStatus[ticket.status].push(ticket);
  }

  return Array.from(map.values())
    .sort((a, b) => a.stationSlug.localeCompare(b.stationSlug))
    .map(sortStatusBuckets);
}
